// Custom ReactFlow node for flow control blocks (purple)
// Handles both "parallel" (fork with N outputs) and "parallel_join" (barrier with single output)
import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { Box, Typography, Chip } from '@mui/material';
import { ForkRight as ParallelIcon, CallMerge as JoinIcon } from '@mui/icons-material';

const PURPLE = '#7b1fa2';
const PURPLE_LIGHT = '#9c27b0';

const FlowControlNode = memo(({ data, selected }) => {
  const isParallel = data.blockType === 'parallel';
  const isJoin = data.blockType === 'parallel_join';
  const branchCount = data.config?.branches || 2;
  const debugStep = data._debugStep;

  const Icon = isParallel ? ParallelIcon : JoinIcon;

  // Debug label for join: show arrival info
  const getDebugLabel = () => {
    if (!debugStep?.output) return null;
    if (isJoin) return 'All arrived';
    if (isParallel) return `${branchCount} branches`;
    return null;
  };

  const debugLabel = debugStep ? getDebugLabel() : null;

  return (
    <Box sx={{
      minWidth: 160,
      border: selected ? `2px solid ${PURPLE}` : `1px solid ${PURPLE_LIGHT}`,
      borderRadius: 1,
      bgcolor: 'background.paper',
      boxShadow: selected ? 3 : 1,
      ...(debugStep && {
        boxShadow: debugStep.status === 'completed'
          ? `0 0 8px rgba(123,31,162,0.4)` : debugStep.status === 'failed'
            ? '0 0 8px rgba(198,40,40,0.4)' : 1,
      }),
    }}>
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: PURPLE, width: 10, height: 10 }}
      />

      {/* Header */}
      <Box sx={{
        px: 1.5, py: 0.75,
        bgcolor: PURPLE,
        color: 'white',
        borderRadius: '3px 3px 0 0',
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
      }}>
        <Icon sx={{ fontSize: 16 }} />
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem' }}>
          {isParallel ? 'Parallel' : 'Join'}
        </Typography>
        {debugStep && (
          <Chip
            label={debugStep.status === 'completed' ? 'OK' : 'ERR'}
            size="small"
            sx={{
              ml: 'auto',
              height: 16,
              fontSize: '0.55rem',
              fontWeight: 700,
              bgcolor: debugStep.status === 'completed' ? 'rgba(255,255,255,0.25)' : 'rgba(198,40,40,0.7)',
              color: 'white',
            }}
          />
        )}
      </Box>

      {/* Body */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
          {data.label || (isParallel ? 'Parallel (Fork)' : 'Join (Wait All)')}
        </Typography>
        {isParallel && (
          <Typography variant="caption" color="text.secondary">
            {branchCount} branches
          </Typography>
        )}
        {isJoin && (
          <Typography variant="caption" color="text.secondary">
            Wait for all branches
          </Typography>
        )}
        {data.summary && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {data.summary}
          </Typography>
        )}

        {debugLabel && (
          <Typography variant="caption" sx={{
            display: 'block',
            fontFamily: 'monospace',
            fontSize: '0.6rem',
            mt: 0.5,
            color: debugStep.status === 'completed' ? PURPLE : '#c62828',
          }}>
            {debugLabel}
          </Typography>
        )}

        {debugStep?.error && (
          <Typography variant="caption" sx={{
            display: 'block',
            fontSize: '0.6rem',
            mt: 0.25,
            color: '#c62828',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 140,
          }}>
            {debugStep.error}
          </Typography>
        )}
      </Box>

      {/* Output handles */}
      {isParallel ? (
        // Multiple output handles for each branch, spaced along the bottom
        Array.from({ length: branchCount }, (_, i) => {
          const offset = ((i + 1) / (branchCount + 1)) * 100;
          return (
            <Handle
              key={`branch-${i}`}
              type="source"
              position={Position.Bottom}
              id={`branch-${i}`}
              style={{
                background: PURPLE,
                width: 8,
                height: 8,
                left: `${offset}%`,
              }}
            />
          );
        })
      ) : (
        // Join: single output handle
        <Handle
          type="source"
          position={Position.Bottom}
          style={{ background: PURPLE, width: 10, height: 10 }}
        />
      )}

      {/* Branch labels for parallel node */}
      {isParallel && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-around',
          px: 0.5,
          pb: 0.5,
        }}>
          {Array.from({ length: branchCount }, (_, i) => (
            <Typography
              key={i}
              variant="caption"
              sx={{
                fontSize: '0.5rem',
                color: 'text.secondary',
                textAlign: 'center',
                flex: 1,
              }}
            >
              {i + 1}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
});

FlowControlNode.displayName = 'FlowControlNode';
export default FlowControlNode;
